// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { createContact } from '@/contact/api/create-contact';
import { getContacts } from '@/contact/api/get-contacts';
import { Contact } from '@/contact/api/types';
import {
  ContactFormData,
  ContactFormOutputData,
  contactFormSchema,
  normalizeNullable,
} from '@/contact/utilities/contact-form-schema';
import { FormCustomerSearch } from '@/customer/components/form-customer-search';
import { getNewCompanyURL } from '@/customer/utilities/get-new-customer-url';
import { SparseCustomer } from '@/sparse/api/types';
import { toCappedSearchTerm } from '@/utilities/search-term';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  SubmitHandler,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';

const DUPLICATE_CHECK_DEBOUNCE_MS = 30;

interface NameToCheck {
  customerId: string;
  firstName: string;
  lastName: string;
}

interface Props {
  isOpen: boolean;
  defaultCustomer?: SparseCustomer | null;
  defaultName?: string;
  onClose: () => void;
  onCreated?: (contact: Contact) => void;
}

function capitalizeWords(value: string): string {
  if (value.length === 0) {
    return value;
  }
  return value
    .split(' ')
    .map((word) =>
      word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
    )
    .join(' ');
}

function splitName(name?: string): { firstName: string; lastName: string } {
  if (!name) {
    return { firstName: '', lastName: '' };
  }
  const trimmed = name.trim();
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) {
    return { firstName: capitalizeWords(trimmed), lastName: '' };
  }
  return {
    firstName: capitalizeWords(trimmed.slice(0, spaceIndex)),
    lastName: capitalizeWords(trimmed.slice(spaceIndex + 1).trim()),
  };
}

function getDefaultValues(
  defaultCustomer?: SparseCustomer | null,
  defaultName?: string
): ContactFormData {
  const { firstName, lastName } = splitName(defaultName);
  return {
    customer: defaultCustomer ?? null,
    firstName,
    lastName,
    title: null,
    email: null,
    phone: null,
    comments: null,
  };
}

export function CreateContactDialog(props: Props) {
  const queryClient = useQueryClient();
  const [emailToCheck, setEmailToCheck] = useState<string | null>(null);
  const [nameToCheck, setNameToCheck] = useState<NameToCheck | null>(null);

  const createContactMutation = useMutation({
    mutationFn: createContact,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contact'] });
      props.onCreated?.(data.contact);
      close();
    },
  });

  const duplicateEmailQuery = useQuery({
    queryKey: ['contact', 'duplicate-email', emailToCheck],
    queryFn: () =>
      getContacts({
        filter: { email: emailToCheck ?? undefined, isArchived: 'all' },
        page: 1,
        limit: 25,
      }),
    enabled: emailToCheck !== null,
  });

  const duplicateNameQuery = useQuery({
    queryKey: ['contact', 'duplicate-name', nameToCheck],
    queryFn: () =>
      getContacts({
        filter: {
          customerIds: nameToCheck ? [nameToCheck.customerId] : undefined,
          search: nameToCheck
            ? toCappedSearchTerm(nameToCheck.firstName)
            : undefined,
          isArchived: 'all',
        },
        page: 1,
        limit: 25,
      }),
    enabled: nameToCheck !== null,
  });

  const { control, reset, handleSubmit } = useForm<
    ContactFormData,
    unknown,
    ContactFormOutputData
  >({
    resolver: zodResolver(contactFormSchema),
    defaultValues: getDefaultValues(props.defaultCustomer, props.defaultName),
  });

  const emailController = useController({ control, name: 'email' });

  const watchedCustomer = useWatch({ control, name: 'customer' });
  const watchedFirstName = useWatch({ control, name: 'firstName' });
  const watchedLastName = useWatch({ control, name: 'lastName' });
  const watchedEmail = useWatch({ control, name: 'email' });

  const debouncedSetNameToCheck = useMemo(
    () =>
      debounce(
        (value: NameToCheck | null) => setNameToCheck(value),
        DUPLICATE_CHECK_DEBOUNCE_MS
      ),
    []
  );

  const debouncedSetEmailToCheck = useMemo(
    () =>
      debounce(
        (value: string | null) => setEmailToCheck(value),
        DUPLICATE_CHECK_DEBOUNCE_MS
      ),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetNameToCheck.cancel();
      debouncedSetEmailToCheck.cancel();
    };
  }, [debouncedSetNameToCheck, debouncedSetEmailToCheck]);

  useEffect(() => {
    debouncedSetEmailToCheck(normalizeNullable(watchedEmail));
  }, [watchedEmail, debouncedSetEmailToCheck]);

  useEffect(() => {
    const firstName = watchedFirstName.trim();
    const lastName = watchedLastName.trim();
    if (!watchedCustomer || firstName === '' || lastName === '') {
      debouncedSetNameToCheck(null);
      return;
    }
    debouncedSetNameToCheck({
      customerId: watchedCustomer.id,
      firstName,
      lastName,
    });
  }, [
    watchedCustomer,
    watchedFirstName,
    watchedLastName,
    debouncedSetNameToCheck,
  ]);

  useEffect(() => {
    if (props.isOpen) {
      reset(getDefaultValues(props.defaultCustomer, props.defaultName));
      debouncedSetEmailToCheck.cancel();
      debouncedSetNameToCheck.cancel();
      setEmailToCheck(null);
      setNameToCheck(null);
    }
  }, [
    props.defaultCustomer,
    props.defaultName,
    props.isOpen,
    reset,
    debouncedSetEmailToCheck,
    debouncedSetNameToCheck,
  ]);

  const close = () => {
    props.onClose();
    reset(getDefaultValues(props.defaultCustomer));
    debouncedSetEmailToCheck.cancel();
    debouncedSetNameToCheck.cancel();
    setEmailToCheck(null);
    setNameToCheck(null);
  };

  const handleCreateCompany = (name: string) => {
    const url = getNewCompanyURL(name);
    window.open(url, '_blank');
  };

  const hasDuplicateEmail =
    (duplicateEmailQuery.data?.contacts.length ?? 0) > 0 &&
    emailToCheck !== null;

  const hasDuplicateName =
    nameToCheck !== null &&
    (duplicateNameQuery.data?.contacts ?? []).some(
      (contact) =>
        contact.firstName.trim().toLowerCase() ===
          nameToCheck.firstName.toLowerCase() &&
        contact.lastName.trim().toLowerCase() ===
          nameToCheck.lastName.toLowerCase()
    );

  const handleValidSubmission: SubmitHandler<ContactFormOutputData> = (
    data
  ) => {
    createContactMutation.mutate({
      customerId: data.customer.id,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      title: normalizeNullable(data.title),
      email: normalizeNullable(data.email),
      phone: normalizeNullable(data.phone),
      comments: normalizeNullable(data.comments),
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: (event: FormEvent) => {
          event.stopPropagation();
          handleSubmit(handleValidSubmission)(event);
        },
      }}
    >
      <SupportSnackbar
        isOpen={
          createContactMutation.isError &&
          !isPlanLimitError(createContactMutation.error)
        }
        message="An error occurred while creating the contact"
        onClose={createContactMutation.reset}
      />
      <DialogTitle>Add contact</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormCustomerSearch
            control={control}
            name="customer"
            label="Company"
            onCreate={handleCreateCompany}
            filter={{ isArchived: false }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormTextField
              fullWidth
              control={control}
              name="firstName"
              label="First name"
            />
            <FormTextField
              fullWidth
              control={control}
              name="lastName"
              label="Last name"
            />
          </Stack>
          {hasDuplicateName && (
            <Alert severity="warning">
              A contact with this name already exists for this company. You can
              still continue.
            </Alert>
          )}
          <FormTextField
            fullWidth
            control={control}
            name="title"
            label="Title"
          />
          <TextField
            fullWidth
            size="small"
            name={emailController.field.name}
            value={emailController.field.value ?? ''}
            label="Email"
            inputRef={emailController.field.ref}
            onChange={emailController.field.onChange}
            onBlur={emailController.field.onBlur}
            error={Boolean(emailController.fieldState.error)}
            helperText={emailController.fieldState.error?.message}
          />
          {hasDuplicateEmail && (
            <Alert severity="warning">
              A contact with this email already exists. You can still continue.
            </Alert>
          )}
          <FormTextField
            fullWidth
            control={control}
            name="phone"
            label="Phone"
          />
          <FormTextField
            fullWidth
            isMultiline
            minimumRows={3}
            control={control}
            name="comments"
            label="Comments"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={createContactMutation.isPending}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          color="secondary"
          variant="contained"
          loading={createContactMutation.isPending}
        >
          Add
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
